import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ROSTER_PAGES,
  isRosterPageId,
  personDisplayName,
  resolvedAssigneeNames,
  rosterConfigError,
  type RosterConfig,
  type RosterPageId,
  type RosterPerson,
  type RosterView
} from "@silph-scope/shared";
import { useMe } from "../api/hooks";
import { ApiError, apiFetch } from "../api/client";

type TabId = RosterPageId | "default";

export function RosterAdminButton({ pageId, variant = "header" }: { pageId?: string; variant?: "header" | "icon" }) {
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  if (!me?.roles.includes("admin")) return null;

  return (
    <>
      <button
        className={variant === "header" ? "btn-ghost" : "icon-btn"}
        type="button"
        title="Edit tracked users"
        aria-label="Edit tracked users"
        onClick={() => setOpen(true)}
      >
        <i className="ri-user-settings-line" />
        {variant === "header" ? "Users" : null}
      </button>
      {open ? <RosterEditor pageId={pageId} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function RosterEditor({ pageId, onClose }: { pageId?: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const roster = useQuery({
    queryKey: ["roster"],
    queryFn: () => apiFetch<RosterView>("/api/roster")
  });
  const [tab, setTab] = useState<TabId>(pageId && isRosterPageId(pageId) ? pageId : "default");
  const [draft, setDraft] = useState<RosterConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (roster.data && !draft) setDraft(structuredClone(roster.data.config));
  }, [roster.data, draft]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const page = tab === "default" ? null : ROSTER_PAGES.find((item) => item.id === tab) ?? null;
  const customList = page ? draft?.pages[page.id] ?? null : null;
  const editing = draft ? (tab === "default" ? draft.default : customList) : null;
  const usingDefault = page != null && customList == null;
  const names = !draft
    ? []
    : tab === "default"
      ? draft.default.map(personDisplayName).filter((name) => name.length > 0)
      : resolvedAssigneeNames(draft, tab);
  const problem = draft ? rosterConfigError(draft) : null;

  async function save() {
    if (!draft || problem) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const savedView = await apiFetch<RosterView>("/api/roster", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(draft)
      });
      setDraft(structuredClone(savedView.config));
      queryClient.setQueryData(["roster"], savedView);
      await queryClient.invalidateQueries({ queryKey: ["panel"] });
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the roster.");
    } finally {
      setSaving(false);
    }
  }

  function updateList(next: RosterPerson[] | null) {
    if (!draft) return;
    setSaved(false);
    if (tab === "default") {
      setDraft({ ...draft, default: next ?? [] });
      return;
    }
    setDraft({ ...draft, pages: { ...draft.pages, [tab]: next } });
  }

  return createPortal(
    <div className="cal-modal-backdrop" onMouseDown={onClose}>
      <div
        className="cal-modal roster-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="roster-editor-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="cal-modal-head">
          <div>
            <div className="eyebrow">Admin</div>
            <h2 id="roster-editor-title">Tracked users</h2>
          </div>
          <button className="icon-btn" type="button" title="Close" onClick={onClose}>
            <i className="ri-close-line" />
          </button>
        </div>
        <p className="roster-lead">
          Each page queries the people on its own list. A page set to fill uses the default list, so a change there
          shows up on every page that has not been customized.
        </p>

        <div className="roster-tabs" role="tablist">
          <button className={tab === "default" ? "active" : ""} type="button" onClick={() => setTab("default")}>
            Default
          </button>
          {ROSTER_PAGES.map((item) => (
            <button key={item.id} className={tab === item.id ? "active" : ""} type="button" onClick={() => setTab(item.id)}>
              {item.name}
              {draft?.pages[item.id] != null ? <span className="roster-custom-mark" title="Own list" /> : null}
            </button>
          ))}
        </div>

        {roster.isError ? (
          <p className="roster-error">{roster.error instanceof Error ? roster.error.message : "Could not load the roster."}</p>
        ) : !draft ? (
          <p className="roster-lead">Loading roster…</p>
        ) : (
          <>
            {usingDefault ? (
              <div className="roster-fill">
                <p>
                  <strong>{page?.name}</strong> is filled from the default list. Queries on this page use those names until
                  you customize it.
                </p>
                <ul className="roster-filled-names">
                  {names.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
                <button className="btn-ghost" type="button" onClick={() => updateList(draft.default.map((person) => ({ ...person })))}>
                  Customize this page
                </button>
              </div>
            ) : (
              <div className="roster-list">
                {(editing ?? []).map((person, index) => (
                  <div className="roster-row" key={index}>
                    <label>
                      <span>First name</span>
                      <input
                        value={person.firstName}
                        autoComplete="off"
                        onChange={(event) =>
                          updateList((editing ?? []).map((row, rowIndex) => (rowIndex === index ? { ...row, firstName: event.target.value } : row)))
                        }
                      />
                    </label>
                    <label>
                      <span>Last name</span>
                      <input
                        value={person.lastName}
                        autoComplete="off"
                        onChange={(event) =>
                          updateList((editing ?? []).map((row, rowIndex) => (rowIndex === index ? { ...row, lastName: event.target.value } : row)))
                        }
                      />
                    </label>
                    <button
                      className="icon-btn"
                      type="button"
                      title={`Remove ${personDisplayName(person) || "person"}`}
                      onClick={() => updateList((editing ?? []).filter((_, rowIndex) => rowIndex !== index))}
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ))}
                <div className="roster-row-actions">
                  <button className="btn-ghost" type="button" onClick={() => updateList([...(editing ?? []), { firstName: "", lastName: "" }])}>
                    <i className="ri-add-line" />
                    Add person
                  </button>
                  {page ? (
                    <button className="btn-ghost" type="button" onClick={() => updateList(null)}>
                      Use default list
                    </button>
                  ) : null}
                </div>
              </div>
            )}

            <p className="roster-query">
              {tab === "default"
                ? "Pages that fill from the default query these names: "
                : "This page’s queries filter on: "}
              <strong>{names.length ? names.join(", ") : "nobody"}</strong>
            </p>
            {page && customList && customList.length === 0 ? (
              <p className="roster-error">This page will not include anyone, so its charts will be empty.</p>
            ) : null}
            {problem ? <p className="roster-error">{problem}</p> : null}
            {error ? <p className="roster-error">{error}</p> : null}
            {saved ? <p className="roster-saved">Saved. Open dashboards will reload with this list.</p> : null}

            <div className="roster-footer">
              <button className="btn-ghost" type="button" onClick={onClose}>
                Close
              </button>
              <button className="btn-primary" type="button" onClick={() => void save()} disabled={saving || !!problem}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
